/**
 * Routes the Atlas Intelligence page reads and writes. Served routes name where the back
 * end defines them (all served). /api/financial/* answers carry no `real` flag (they are
 * catalog and model reads, labelled MODELED on the page where they are modeled), so those
 * routes are not `honest`.
 */
import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  AtlasAnalystResult,
  BenchmarksResponse,
  CatalogBreakdown,
  CatalogReach,
  CmsFacilities,
  CmsLookup,
  CmsScore,
  DcfResult,
  DealLeversResponse,
  DistressResponse,
  FacilitiesPage,
  FacilitiesQuery,
  FacilityBacktest,
  FacilityGeoPage,
  FacilityOptimize,
  IntelThread,
  MonteCarloResult,
  OperatorsResponse,
  PaperBook,
  PaperPositionDetail,
  ProspectsResponse,
  SemanticSearchResult,
  SourcingSummary,
  WatchResult,
  MacroRates,
} from './intelligence-types';

const F = '/api/financial';
const A = AITHORA_PREFIX;

// ── The deal book and the catalog (src/financial/router.ts) ─────────────────

/** GET /prospects: the prospecting board (tracked, evaluating, pursue, contacted, acquired, pass). */
export const intelProspects = route<ProspectsResponse, { status?: string }>()('GET', `${F}/prospects`);
/** GET /facilities/breakdown: catalog totals by type and by residential / agency. */
export const intelBreakdown = route<CatalogBreakdown>()('GET', `${F}/facilities/breakdown`);
/** GET /intelligence/reach (v3/atlas/intel-money.ts): scored, enriched, decision-makers and tracked counts. */
export const intelReach = route<CatalogReach>()('GET', `${A}/intelligence/reach`, { honest: true });
/** GET /facilities: the catalog, filtered and sorted server side. */
export const intelFacilities = route<FacilitiesPage, FacilitiesQuery>()('GET', `${F}/facilities`);
/** GET /facilities/geo?lens=: every facility with coordinates, paged (100,000 a page). */
export const intelGeo = route<FacilityGeoPage, { lens?: string; offset?: number; limit?: number }>()('GET', `${F}/facilities/geo`);
/**
 * GET /operators: the ownership graph, one row per operator. `q` searches the operator
 * name; `minFacilities` (default 2) is the smallest chain listed; limit caps at 200.
 */
export const intelOperators = route<
  OperatorsResponse,
  { state?: string; q?: string; minFacilities?: number; sort?: 'facilities' | 'beds' | 'distress' | 'opportunity' | 'rating' | 'name'; order?: 'asc' | 'desc'; limit?: number; offset?: number }
>()('GET', `${F}/operators`);
/** GET /facilities/distress: the distress screen. */
export const intelDistress = route<DistressResponse, { state?: string; limit?: number; offset?: number }>()('GET', `${F}/facilities/distress`);
/** GET /facilities/benchmarks?type=&state=&bedBand=: peer-group p10 to p90 per CMS metric. */
export const intelBenchmarks = route<BenchmarksResponse, { type?: string; state?: string; bedBand?: string }>()('GET', `${F}/facilities/benchmarks`);
/** POST /facilities/search-semantic: meaning-based catalog search (needs the embedding key). */
export const intelSemantic = route<SemanticSearchResult, void, { q: string; limit?: number; state?: string }>()('POST', `${F}/facilities/search-semantic`, { readOnlySafe: true });
/** GET /intelligence/sourcing (v3/atlas/intel-money.ts): the screened candidate set's counts and pipeline EV. */
export const intelSourcing = route<SourcingSummary>()('GET', `${A}/intelligence/sourcing`, { honest: true });

// ── Paper trade ─────────────────────────────────────────────────────────────

/** GET /paper-positions: the open paper book (operator: network wide). */
export const intelPaperBook = route<PaperBook>()('GET', `${F}/paper-positions`);
/** GET /paper-positions/:id: the position, its realized-vs-modeled drift and Atlas variant. */
export const intelPaperPosition = route<PaperPositionDetail>()('GET', `${F}/paper-positions/:id`);
/** GET /facilities/:id/backtest: entry at the earliest HCRIS year, marked to the latest actuals. */
export const intelBacktest = route<FacilityBacktest>()('GET', `${F}/facilities/:id/backtest`, { acceptStatuses: [404] });
/** POST /facilities/:id/optimize: the lever optimizer, with its best `combos`. */
export const intelOptimize = route<FacilityOptimize, void, { objective?: 'irr' | 'moic' | 'minRisk'; combos?: number }>()('POST', `${F}/facilities/:id/optimize`, { acceptStatuses: [404], readOnlySafe: true });

// ── Scenario ────────────────────────────────────────────────────────────────

/** POST /deal-levers?appetite=: the legal value stack (live research, or the static library). */
export const intelDealLevers = route<DealLeversResponse, { appetite?: string }, { facility: { name?: string; state?: string; beds?: number; type?: string }; risk?: string }>()(
  'POST',
  `${F}/deal-levers`,
  { readOnlySafe: true },
);

// ── Analysis: live runs (all read-only computations) ────────────────────────

/** GET /macro: live FRED rates and the suggested WACC every deal DCF discounts at. */
export const intelMacro = route<MacroRates>()('GET', `${F}/macro`);

/** POST /dcf */
export const intelDcf = route<DcfResult, void, { initialCashFlow: number; growthRate?: number; discountRate?: number; terminalGrowthRate?: number; projectionYears?: number; ccn?: string }>()(
  'POST',
  `${F}/dcf`,
  { readOnlySafe: true },
);
/** POST /monte-carlo (EV mode: target 'ev', bins, askEv). */
export const intelMonteCarlo = route<
  MonteCarloResult,
  void,
  { baseRevenue: number; growthRate?: number; volatility?: number; years?: number; simulations?: number; target?: 'ev'; ev?: number; askEv?: number; bins?: number }
>()('POST', `${F}/monte-carlo`, { readOnlySafe: true });
/** GET /cms-lookup/:providerId */
export const intelCmsLookup = route<CmsLookup>()('GET', `${F}/cms-lookup/:providerId`, { acceptStatuses: [404] });
/** POST /cms-score/:providerId */
export const intelCmsScore = route<CmsScore, void, Record<string, never>>()('POST', `${F}/cms-score/:providerId`, { acceptStatuses: [404], readOnlySafe: true });
/** GET /cms-facilities */
export const intelCmsFacilities = route<CmsFacilities, { state?: string; limit?: number; offset?: number }>()('GET', `${F}/cms-facilities`);
/** POST /atlas-analyst: the acquisitions analyst (full memo budget). */
export const intelAnalyst = route<AtlasAnalystResult, void, { question: string; history?: { role: 'user' | 'assistant'; content: string }[] }>()(
  'POST',
  `${F}/atlas-analyst`,
  { readOnlySafe: true },
);
/** GET /facilities/:id/summary?format=pdf: the deal summary (IC memo) as a PDF. */
export function intelSummaryPdfPath(id: string): string {
  return `${F}/facilities/${encodeURIComponent(id)}/summary?format=pdf`;
}

// ── Watch (src/aithora/v3/atlas/intel.ts) ───────────────────────────────────

/** POST /intelligence/watch: sealed to the Truth Ledger. */
export const intelWatch = route<WatchResult, void, { facilityIds: string[] }>()('POST', `${A}/intelligence/watch`, { honest: true });

// ── The Atlas tab ───────────────────────────────────────────────────────────

/** GET /intelligence/atlas/thread (v3/atlas/intel-money.ts): this person's catalog thread. */
export const intelThread = route<IntelThread>()('GET', `${A}/intelligence/atlas/thread`, { honest: true });

/** What POST /api/atlas/ask streamed, folded into one answer. */
export interface IntelAskResult {
  answer: string;
  withheld: boolean;
  tools: string[];
  cites: string[];
  gate: 'PASS' | 'HOLD' | 'BLOCK' | null;
  live: boolean;
  /** Who wrote the answer (route.provider / route.model). */
  provider: string | null;
  model: string | null;
  /** Who ran live web research for it, and its source URLs (the citations event). */
  research: string | null;
  webSources: string[];
}

/**
 * POST /api/atlas/ask (SSE, src/server.ts): answer-chunk, answer, trace, gate, citations,
 * done. Reads the stream and folds it into one answer; onChunk gets each streamed sentence.
 * Sent with thread 'intelligence', so the turn is kept on the asker's Intelligence thread.
 */
export async function intelAsk(
  client: ApiClient,
  body: { question: string; history?: { role: 'user' | 'assistant'; content: string }[] },
  opts: { onChunk?: (text: string) => void; signal?: AbortSignal } = {},
): Promise<IntelAskResult> {
  const res = await client.raw('/api/atlas/ask', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ ...body, thread: 'intelligence' }),
    signal: opts.signal,
  });
  if (!res.ok) {
    let msg = `Atlas could not answer (${res.status}).`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* not JSON */
    }
    throw Object.assign(new Error(msg), { status: res.status, requestId: res.headers.get('x-request-id') });
  }
  const out: IntelAskResult = { answer: '', withheld: false, tools: [], cites: [], gate: null, live: false, provider: null, model: null, research: null, webSources: [] };
  let failure: string | null = null;
  let got = false;
  const text = await res.text();
  for (const block of text.split(/\n\n+/)) {
    const ev = /^event: (.+)$/m.exec(block)?.[1]?.trim();
    const data = /^data: (.*)$/m.exec(block)?.[1];
    if (!ev || data === undefined) continue;
    let d: Record<string, unknown>;
    try {
      d = JSON.parse(data) as Record<string, unknown>;
    } catch {
      continue;
    }
    if (ev === 'answer-chunk') opts.onChunk?.(String(d.text ?? ''));
    else if (ev === 'answer') {
      got = true;
      out.answer = String(d.answer ?? '');
      out.withheld = !!d.withheld;
      const route = d.route as { live?: boolean; provider?: string; model?: string } | undefined;
      out.live = !!route?.live;
      out.provider = typeof route?.provider === 'string' ? route.provider : null;
      out.model = typeof route?.model === 'string' ? route.model : null;
    } else if (ev === 'trace') {
      const tools = Array.isArray(d.tools) ? d.tools : [];
      out.tools = tools.map((t) => (typeof t === 'string' ? t : String((t as { name?: string; tool?: string }).name ?? (t as { tool?: string }).tool ?? ''))).filter(Boolean);
    } else if (ev === 'gate') {
      const r = String(d.result ?? '');
      out.gate = r === 'PASS' || r === 'HOLD' || r === 'BLOCK' ? r : null;
    } else if (ev === 'citations') {
      out.cites = Array.isArray(d.regulationsCited) ? d.regulationsCited.map(String) : [];
      out.research = typeof d.research === 'string' && d.research ? d.research : null;
      out.webSources = Array.isArray(d.webSources) ? d.webSources.map(String).filter((u) => /^https?:\/\//.test(u)) : [];
    } else if (ev === 'error') failure = String(d.error ?? 'Atlas stopped part way.');
  }
  if (!got) throw new Error(failure ?? 'Atlas sent no answer.');
  return out;
}

// ── Live research (financial/facility-research.ts), restored from production ──────
// Perplexity finds what is reported, sourced; Claude gives a one-line read. Cached a day.

export interface ResearchFinding {
  text: string;
  date: string | null;
  sourceDate: string | null;
  category: string;
  material: boolean;
  sources: string[];
}
export interface ResearchResult {
  live: boolean;
  subject: { kind: 'facility' | 'operator' | 'market'; name: string };
  findings: ResearchFinding[];
  sources: string[];
  verdict: { stance: string | null; line: string; /** Aithora roles only. */ provider?: string; model?: string } | null;
  asOf: string;
  cached: boolean;
  note?: string;
}
/** GET /market-brief?refresh=1: what moved senior-care money this quarter (Finance, Aithora scope). */
export const marketBrief = route<ResearchResult, { refresh?: string }>()('GET', `${F}/market-brief`);
/** GET /facilities/:id/research?refresh=1: what is reported about one facility. */
export const facilityResearch = route<ResearchResult, { refresh?: string }>()('GET', `${F}/facilities/:id/research`);
/** One of an operator's buildings, as GET /operators/:org serves it (numerics may arrive as strings). */
export interface OperatorBuilding {
  id: string; ccn: string | null; name: string; city: string | null; state: string | null; beds: number | null;
  type?: string | null; unit_type?: string | null; overall_rating: number | null; composite_score?: number | string | null;
  score_grade: string | null; market_score?: number | string | null; opportunity_score?: number | string | null;
  occupancy?: number | string | null; operating_margin?: number | string | null; abuse_icon?: boolean | null;
  severe_deficiency_count?: number | null; pe_backed?: boolean | null; latitude?: number | string | null; longitude?: number | string | null;
}
/**
 * GET /operators/:org: one operator drilled down (restored from production's operator
 * detail and relationship network). `real` and `source`: the buildings and the owner_org
 * that groups them are CMS-reported, so the page badges them MEASURED. `owners` are the
 * CMS owner rows (organizations with an ownership or control role); `ownersAvailable`
 * false (with `ownersReason`) means they are not known, never that there are none.
 */
export interface OperatorDetail {
  real?: boolean;
  source?: 'CMS';
  summary: {
    ownerOrg: string; facilityCount: number; states: string[]; totalBeds: number; avgRating: number | null;
    avgComposite: number | null; avgOpportunity: number | null; peBacked: boolean; distressCount: number;
  };
  facilities: OperatorBuilding[];
  owners: Array<{ name: string; roles: string[]; buildings: number; maxPct: number | null; peLike: boolean }>;
  ownersAvailable?: boolean;
  ownersReason?: string;
}
export const intelOperatorDetail = route<OperatorDetail>()('GET', `${F}/operators/:org`);
/** GET /operators/:org/reimbursement: the operator's MODELED opportunity across its buildings. */
export const intelOperatorReimbursement = route<{ ownerOrg: string; modeled: true; buildings: number; reimbursementAnnual: number; opportunityAnnual: number }>()(
  'GET', `${F}/operators/:org/reimbursement`);
/** GET /backtest/cohort?state=: the turnaround cohort, did margin gains come with occupancy (restored from production). */
export interface CohortBacktest {
  available: boolean; reason?: string; n?: number; cohortN?: number;
  cohort?: { avgMarginDelta: number; avgOccupancyDelta: number | null; avgRevenueCagr: number; pctImproved: number };
  laggards?: { n: number; avgMarginDelta: number }; note?: string;
}
export const intelCohortBacktest = route<CohortBacktest, { state?: string }>()('GET', `${F}/backtest/cohort`);
/** GET /operators/:org/backtest: did its buildings improve (HCRIS pairs)? available:false below the minimum. */
export interface OperatorBacktestResult {
  available: boolean; reason?: string; n?: number; avgRevenueCagr?: number; avgMarginDelta?: number;
  pctMarginImproved?: number; note?: string;
}
export const intelOperatorBacktest = route<OperatorBacktestResult>()('GET', `${F}/operators/:org/backtest`);
/** GET /operators/:org/research?refresh=1: what is reported about an operator. */
export const operatorResearch = route<ResearchResult, { refresh?: string }>()('GET', `${F}/operators/:org/research`);
